import React from "react";
import { useLang } from "../context/LangContext";
import PageHeader from "../components/site/PageHeader";
import { IMG, VALUES, COMPANY } from "../lib/siteData";
import { Target, Compass, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const About = () => {
  const { t } = useLang();
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow={t("nav.about")} title={t("about.title")} subtitle={t("about.subtitle")} image={IMG.industrialFacility} />

      {/* Background */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-4">{t("about.backgroundLabel")}</div>
            <h2 className="section-title text-3xl sm:text-4xl mb-6">An engineering, procurement and project development partner for the industrial sector.</h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-ink-muted leading-relaxed text-base lg:text-lg">
            <p>
              {COMPANY.name} (Arabic: ويفز تكنولوجي) is a UAE-registered company operating under IFZA, Dubai, with a focus on industrial, oil & gas, water treatment and infrastructure projects across the Middle East — particularly Iraq, Egypt and the wider Gulf region.
            </p>
            <p>
              The company provides technical consulting, engineering support services, procurement support, vendor and technology coordination, equipment sourcing, project development support and water treatment technology solutions — engaging on inspection and commissioning support through project-specific arrangements.
            </p>
            <p>
              Waves Technology FZCO acts as a technical interface between clients and a network of qualified international manufacturers and technology providers — supporting concept development, feasibility, technical evaluation, procurement coordination and execution support phases.
            </p>
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section className="py-20 bg-steel-50 border-y border-steel-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-steel-200 border border-steel-200">
          <div className="bg-white p-10 indicator-line" data-testid="vision-block">
            <Target className="w-9 h-9 text-brand mb-5" strokeWidth={1.4} />
            <div className="overline mb-3">{t("about.visionLabel")}</div>
            <p className="font-heading text-2xl text-ink leading-tight">
              To be a reliable regional partner for engineering, procurement and project development across industrial and water treatment sectors.
            </p>
          </div>
          <div className="bg-white p-10 indicator-line" data-testid="mission-block">
            <Compass className="w-9 h-9 text-brand mb-5" strokeWidth={1.4} />
            <div className="overline mb-3">{t("about.missionLabel")}</div>
            <p className="font-heading text-2xl text-ink leading-tight">
              Deliver engineering, procurement and technical consultancy services to recognised international standards, with realistic scope, transparent commercial conduct and timely execution.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-4">{t("about.valuesLabel")}</div>
            <h2 className="section-title text-3xl sm:text-4xl">The principles that guide our engineering and procurement work.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-steel-200 border border-steel-200">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-white p-8 indicator-line" data-testid={`value-${i}`}>
                  <Icon className="w-7 h-7 text-brand mb-5" strokeWidth={1.5} />
                  <h3 className="font-heading font-bold text-lg text-ink mb-2">{v.title}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <h3 className="font-heading text-2xl lg:text-3xl max-w-2xl">Ready to discuss engineering, procurement or project development support for your next project?</h3>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold text-sm px-7 py-4 uppercase tracking-[0.18em]" data-testid="about-contact-cta">
            {t("common.contactUs")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
